import React, { useRef, useEffect, useState, useLayoutEffect } from 'react'
import { Message } from 'ai'
import { ArrowDown } from 'lucide-react'

import { ChatMessage } from '@/components/chat/ChatMessage'

type MessageListProps = {
  messages: Message[]
}

const MessageList: React.FC<MessageListProps> = ({ messages }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  const [arrowLeft, setArrowLeft] = useState<number | null>(null)

  const isAtBottom = () => {
    const container = containerRef.current
    if (!container) return true

    // Consider we are at bottom only when the user hasn't scrolled up at all (≤ 8 px)
    const threshold = 8
    return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold
  }

  const handleScroll = () => {
    setShouldAutoScroll(isAtBottom())
  }

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, shouldAutoScroll])

  // Calculate center X of the visible chat area to place the arrow accurately.
  useLayoutEffect(() => {
    const updateArrowPosition = () => {
      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      setArrowLeft(rect.left + rect.width / 2)
    }

    updateArrowPosition()
    window.addEventListener('resize', updateArrowPosition)
    return () => window.removeEventListener('resize', updateArrowPosition)
  }, [])

  console.log('frontend messages', messages)

  const handleScrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    setShouldAutoScroll(true)
  }

  return (
    <>
      <div ref={containerRef} className="flex-1 overflow-y-auto px-10 md:px-16 lg:px-24 xl:px-32 pt-topbar pb-6">
        <div className="w-full max-w-[950px] mx-auto">
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Floating "scroll to bottom" arrow */}
      {!shouldAutoScroll && arrowLeft !== null && (
        <button
          onClick={handleScrollToBottom}
          style={{ left: arrowLeft, transform: 'translateX(-50%)' }}
          className="fixed bottom-28 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-black text-white shadow-lg hover:bg-gray-900 focus:outline-none"
          aria-label="Scroll to newest messages"
        >
          <ArrowDown className="h-5 w-5" />
        </button>
      )}
    </>
  )
}

export default MessageList
